/* eslint-disable no-unused-vars */
// This will automatically include the polyfill.
import FetchInit from 'fetch-ie8';
/* eslint-enable no-unused-vars */

import Browser from 'browser';
import URLBuilder from 'url-builder';
import groupBy from './utils/group-by';

export default class GwiApi {
  constructor(apiKey, authToken, gwiHost) {
    this.apiKey = apiKey;
    this.authToken = authToken;
    this.gwiHost = gwiHost;
  }

  getBricksForBrickData(brickDatas) {
    const groupedByWatchableType = groupBy(brickDatas, 'watchableType');

    const brickEndpointQueryParams = {};
    Object.keys(groupedByWatchableType).forEach((watchableType) => {
      brickEndpointQueryParams[`${watchableType.toLowerCase()}_ids`] = groupedByWatchableType[watchableType].map(config => config.watchableId);
    });

    return this.getBricks(brickEndpointQueryParams);
  }

  search(term, params = {}) {
    const path = '/api/v3/search';

    return this.get(path, { term, ...params });
  }

  getBricks(params) {
    const path = '/api/v3/bricks';

    return this.get(path, params);
  }

  setUserRating(watchableType, watchableId, rating) {
    const path = `/api/v3/${watchableType.toLowerCase()}s/${watchableId}/rate/${rating}`;

    return this.post(path);
  }

  // Session
  // ***************************
  login(email, password) {
    const path = '/api/v3/session/with_password';

    const body = {
      email,
      password,
    };

    return this.post(path, body);
  }

  logout() {
    const path = '/api/v3/session/logout';

    return this.get(path, {});
  }

  // User
  // ***************************
  fetchUser() {
    const path = '/api/v3/user/me';

    return this.get(path, {});
  }

  getUserPartners() {
    const path = '/api/v3/user/project_partners';

    return this.get(path);
  }
  // ***************************

  getProjects(partnerId) {
    const path = `/api/v3/partners/${partnerId}/projects`;

    return this.get(path);
  }

  getProject(partnerId, projectId) {
    const path = `/api/v3/partners/${partnerId}/projects/${projectId}`;

    return this.get(path);
  }

  getAffiliateTransactions(partnerId, params) {
    const path = `/api/v3/partners/${partnerId}/affiliate_transactions`;

    return this.get(path, params);
  }

  getTheaterList(partnerId, projectId) {
    const path = `/api/v3/partners/${partnerId}/theater_groupings/theater_list`;
    const params = {
      project_id: projectId,
    };

    return this.get(path, params);
  }

  uploadTheaterList(partnerId, body={}) {
    const path = `/api/v3/partners/${partnerId}/theater_groupings/theater_list_import`;

    return this.put(path, {}, body, { ignoreStringify: true });
  }

  // Watchable
  // ***************************
  getWatchable(watchableType, watchableId, params = {}) {
    const path = `/api/v3/${watchableType.toLowerCase()}s/${watchableId}/superwidget`;

    return this.get(path, params);
  }

  async getWatchableAvailabilities(watchableType, watchableId, params = {}) {
    const path = `/api/v3/${watchableType.toLowerCase()}s/${watchableId}/availabilities`;

    return this.get(path, params);
  }

  getMovie(movieId) {
    const path = `/api/v3/movies/${movieId}`;

    return this.get(path);
  }

  getShow(showId) {
    const path = `/api/v3/shows/${showId}`;

    return this.get(path);
  }

  getSeason(seasonId) {
    const path = `/api/v3/seasons/${seasonId}`;

    return this.get(path);
  }

  getEpisode(episodeId) {
    const path = `/api/v3/episodes/${episodeId}`;

    return this.get(path);
  }
  
  getLatestAvailableEpisode(showId) {
    const path = `/api/v3/shows/${showId}/latest_available_episode`;

    return this.get(path);
  }

  getMovieTheatrical(movieId) {
    const path = `/api/v3/movies/${movieId}/availabilities/theatrical`;

    return this.get(path);
  }

  getTvTuneIn(episodeId) {
    const path = `/api/v3/service/schedule?episode_id=${episodeId}`;

    return this.get(path);
  }

  addToQueue(watchableType, watchableId) {
    const path = '/api/v3/user/queue';
    const params = {
      watchable_type: watchableType,
      watchable_id: watchableId,
    };

    return this.post(path, params);
  }

  removeFromQueue(watchableType, watchableId) {
    const path = '/api/v3/user/queue';
    const params = {
      watchable_type: watchableType,
      watchable_id: watchableId,
    };

    return this.delete(path, params);
  }

  getList(listId) {
    const path = `/api/v3/lists/${listId}`;

    return this.get(path);
  }

  getLists() {
    const path = '/api/v3/lists';

    return this.get(path);
  }

  // This call just gets the watchable instead of hitting the superwidget endpoint
  getWatchableMetadata(watchableId, watchableType) {
    const path = `/api/v3/${watchableType.toLowerCase()}s/${watchableId}`;

    return this.get(path);
  }

  addWatchableToList(listId, listableId, listableType, tagId) {
    const tag = tagId || null;
    const path = `/api/v3/lists/add_item/${listId}`;

    const body = {
      list_item: {
        listable_id: listableId,
        listable_type: listableType,
        list_tag_id: tag,
      },
    };

    return this.post(path, {}, body);
  }

  removeWatchableFromList(listId, listItemId) {
    const path = `/api/v3/lists/delete_item/${listId}/${listItemId}`;

    return this.post(path);
  }

  getUserRating(id, type) {
    const path = `/api/v3/${type.toLowerCase()}s/${id}/user_rating`;

    return this.get(path);
  }

  eraseUserRating(id, type) {
    const path = `/api/v3/${type.toLowerCase()}s/${id}/unrate`;

    return this.delete(path);
  }

  getBrick(type, id) {
    const params = {};

    // Set movie_ids = [movie_id]
    params[`${type.toLowerCase()}_ids`] = [id];

    return this.getBricks(params);
  }


  // ************************************************
  //                  Private Methods
  // ************************************************

  async query(method, path, params, body, options) {
    const headers = new Headers({
      'X-api-key': this.apiKey,
      'X-gwi-asset': true,
    });

    if (this.authToken) {
      headers.append('X-auth-token', this.authToken);
    }

    const requestOptions = {
      method,
      headers,
    };

    // https://stackoverflow.com/a/32108184/2341704
    const isEmptyObject = Object.keys(body).length === 0 && body.constructor === Object;
    if (method !== 'GET' && !isEmptyObject) {
      requestOptions.body = JSON.stringify(body);
    }

    if (options.ignoreStringify && !isEmptyObject) {
      requestOptions.body = body;
    }

    const request = new Request(URLBuilder.buildUrl(this.gwiHost, path, params), requestOptions);

    return GwiApi.request(request);
  }

  async get(path, params = {}, options = {}) {
    return this.query('GET', path, params, {}, options);
  }

  post(path, params = {}, body = {}, options = {}) {
    return this.query('POST', path, params, body, options);
  }

  put(path, params = {}, body = {}, options = {}) {
    return this.query('PUT', path, params, body, options);
  };

  delete(path, params = {}, options = {}) {
    return this.query('DELETE', path, params, {}, options);
  }

  static async request(requestObject) {
    return Browser.getWindow().fetch(requestObject).then(async (response) => {
      const parsedResponse = await response.json();
      return {
        ...parsedResponse,
        status: response.status,
      };
    });
  }

  static get(host, path, params) {
    return Browser.getWindow().fetch(URLBuilder.buildUrl(host, path, params))
        .then(response => response.json());
  }
}



// WEBPACK FOOTER //
// ./~/gwi-api/src/gwi-api.js